import React, { useState, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Coffee, Users, Video, BookOpen, Plus, Calendar, Clock, MapPin, X, ChevronDown, User, CheckCircle2, MessageSquare, Search, Filter, Home, Globe, Zap, Info, HelpCircle, ArrowLeft, ChevronRight, Briefcase, Leaf } from 'lucide-react';
export function NetworkingDetailPage() {
  const navigate = useNavigate();
  const {
    mode
  } = useParams();
  const [activeTab, setActiveTab] = useState<'all' | 'my'>('all');
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    location: ''
  });
  const networkingModes = {
    coffee: {
      id: 'coffee',
      title: 'Coffee Break',
      description: 'Meet people with similar interests for a quick and informal coffee',
      icon: Coffee,
      modalTitle: 'New Coffee Break'
    },
    lunch: {
      id: 'lunch',
      title: 'Topic Lunch',
      description: 'Themed lunches with multiple people from the same location',
      icon: Users,
      modalTitle: 'New Topic Lunch'
    },
    call: {
      id: 'call',
      title: 'Call 1-to-1',
      description: 'Video calls with AI listening for summaries and suggestions',
      icon: Video,
      modalTitle: 'New Call 1-to-1'
    },
    workshop: {
      id: 'workshop',
      title: 'Workshop',
      description: 'Cross-location workshops on specific topics with targeted objectives',
      icon: BookOpen,
      modalTitle: 'New Workshop'
    }
  };
  const currentMode = mode && networkingModes[mode] ? networkingModes[mode] : null;
  if (!currentMode) {
    return <div className="flex flex-col min-h-screen w-full bg-white text-black font-sans pt-6" data-id="element-1842">
        <div className="max-w-6xl mx-auto px-4 md:px-6" data-id="element-1843">
          <h1 className="text-2xl md:text-3xl font-light mb-4" data-id="element-1844">
            Mode not found
          </h1>
          <button onClick={() => navigate('/networking')} className="flex items-center text-black hover:underline" data-id="element-1845">
            <ArrowLeft className="h-4 w-4 mr-1" data-id="element-1846" />
            Back to Networking
          </button>
        </div>
      </div>;
  }
  const ModeIcon = currentMode.icon;
  const openCreateModal = () => {
    setShowCreateModal(true);
  };
  const closeModal = () => {
    setShowCreateModal(false);
  };
  const handleFilterChange = (key: string, value: string) => {
    setFilters({
      ...filters,
      [key]: value
    });
  };
  const resetFilters = () => {
    setFilters({
      status: '',
      location: ''
    });
  };
  const locations = ['Rome', 'Milan', 'Florence', 'Turin', 'Naples'];
  const topics = ['Design', 'Development', 'Marketing', 'Business', 'AI', 'Blockchain', 'Data Science', 'UX/UI', 'Product Management'];
  // Sample data for Coffee Break requests
  const coffeeBreakRequests = [{
    id: 1,
    user: {
      name: 'Marco Bianchi',
      role: 'UX Designer',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rpDp7vdFb4PQ6LshFeTqC4/christian-buehner-zYD6ou1Qj10-unsplash.jpg'
    },
    topic: 'Design System and UI Components',
    tags: ['Design', 'UX/UI']
  }, {
    id: 2,
    user: {
      name: 'Laura Rossi',
      role: 'Product Manager',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/iiGCarC4BpomqUoZERcuSH/jurica-koletic-7YVZYZeITc8-unsplash.jpg'
    },
    topic: 'Product Management Strategies',
    tags: ['Product', 'Management']
  }, {
    id: 3,
    user: {
      name: 'Andrea Marino',
      role: 'Data Scientist',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bc8MUYcKAT6QrAiyMeUwXx/christian-buehner-DItYlc26zVI-unsplash.jpg'
    },
    topic: 'Machine Learning Applied to Business',
    tags: ['AI', 'Data Science']
  }, {
    id: 4,
    user: {
      name: 'Giulia Verdi',
      role: 'Web Developer',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gou2Ede34nLvCQizNZLz4q/irene-strong--FOUPtqP-mY-unsplash.jpg'
    },
    topic: 'Modern Frontend Architectures',
    tags: ['Development', 'Frontend']
  }, {
    id: 5,
    user: {
      name: 'Roberto Neri',
      role: 'Marketing',
      image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/37RNAboQN8qk3BnTK1YzhT/irene-strong-TMt3JGoVlng-unsplash.jpg'
    },
    topic: 'Growth Marketing Strategies',
    tags: ['Marketing', 'Growth']
  }];
  // Render content based on mode and activeTab
  const renderContent = () => {
    // If we're in the "Requests" tab and mode is "coffee", show Coffee Break requests
    if (activeTab === 'my' && mode === 'coffee') {
      return <div className="space-y-4" data-id="element-1847">
          <div className="text-sm text-gray-500 mb-2" data-id="element-1848">
            Coffee Break requests opened by other users
          </div>
          {coffeeBreakRequests.map(request => <div key={request.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow" data-id="element-1849">
              <div className="flex items-start gap-3" data-id="element-1850">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-1851">
                  <img src={request.user.image} alt={request.user.name} className="w-full h-full object-cover" data-id="element-1852" />
                </div>
                <div className="flex-1" data-id="element-1853">
                  <div className="flex justify-between items-start mb-2" data-id="element-1854">
                    <div data-id="element-1855">
                      <h3 className="font-medium text-base mb-1" data-id="element-1856">
                        {request.topic}
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-1857">
                        Proposed by {request.user.name} • {request.user.role}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-3" data-id="element-1858">
                    {request.tags.map((tag, index) => <span key={index} className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-1859">
                        {tag}
                      </span>)}
                  </div>
                  <div className="flex gap-2" data-id="element-1860">
                    <button className="bg-black text-white text-xs px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors flex items-center" data-id="element-1861">
                      <Coffee className="h-3.5 w-3.5 mr-1" data-id="element-1862" />
                      Join
                    </button>
                  </div>
                </div>
              </div>
            </div>)}
        </div>;
    }
    // Otherwise, continue with existing logic based only on mode
    switch (mode) {
      case 'coffee':
        return <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1863">
            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow" data-id="element-1864">
              <div className="flex items-start gap-3" data-id="element-1865">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-1866">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iiGCarC4BpomqUoZERcuSH/jurica-koletic-7YVZYZeITc8-unsplash.jpg" alt="Laura R." className="w-full h-full object-cover" data-id="element-1867" />
                </div>
                <div className="flex-1" data-id="element-1868">
                  <div className="mb-2" data-id="element-1869">
                    <h3 className="font-medium text-base mb-1" data-id="element-1870">
                      Coffee Break: Artificial Intelligence
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-1871">
                      Practical applications of AI in business projects
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mb-3" data-id="element-1872">
                    <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full" data-id="element-1873">
                      IT
                    </span>
                    <span className="bg-purple-50 text-purple-700 text-xs px-2 py-1 rounded-full" data-id="element-1874">
                      Innovation
                    </span>
                  </div>
                  <div className="border-t border-gray-100 pt-2 mb-3" data-id="element-1875">
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600" data-id="element-1876">
                      <div className="flex items-center" data-id="element-1877">
                        <Calendar className="h-3.5 w-3.5 mr-1 flex-shrink-0" data-id="element-1878" />
                        <span className="truncate" data-id="element-1879">Friday 29/10</span>
                      </div>
                      <div className="flex items-center" data-id="element-1880">
                        <Clock className="h-3.5 w-3.5 mr-1 flex-shrink-0" data-id="element-1881" />
                        <span className="truncate" data-id="element-1882">15:00</span>
                      </div>
                      <div className="flex items-center col-span-2" data-id="element-1883">
                        <span className="text-gray-700 font-medium" data-id="element-1884">With:</span>
                        <span className="ml-1" data-id="element-1885">Laura R. (Host)</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-1886">
                    <button className="bg-black text-white text-xs px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors flex items-center" data-id="element-1887">
                      <Coffee className="h-3.5 w-3.5 mr-1" data-id="element-1888" />
                      Join
                    </button>
                    <button className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1889">
                      Reschedule
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow" data-id="element-1890">
              <div className="flex items-start gap-3" data-id="element-1891">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-1892">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bc8MUYcKAT6QrAiyMeUwXx/christian-buehner-DItYlc26zVI-unsplash.jpg" alt="Andrea M." className="w-full h-full object-cover" data-id="element-1893" />
                </div>
                <div className="flex-1" data-id="element-1894">
                  <div className="mb-2" data-id="element-1895">
                    <h3 className="font-medium text-base mb-1" data-id="element-1896">
                      Coffee Break: Blockchain
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-1897">
                      Recent developments and practical use cases
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mb-3" data-id="element-1898">
                    <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full" data-id="element-1899">
                      IT
                    </span>
                    <span className="bg-purple-50 text-purple-700 text-xs px-2 py-1 rounded-full" data-id="element-1900">
                      Innovation
                    </span>
                  </div>
                  <div className="border-t border-gray-100 pt-2 mb-3" data-id="element-1901">
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600" data-id="element-1902">
                      <div className="flex items-center" data-id="element-1903">
                        <Calendar className="h-3.5 w-3.5 mr-1 flex-shrink-0" data-id="element-1904" />
                        <span className="truncate" data-id="element-1905">Monday 18/10</span>
                      </div>
                      <div className="flex items-center" data-id="element-1906">
                        <Clock className="h-3.5 w-3.5 mr-1 flex-shrink-0" data-id="element-1907" />
                        <span className="truncate" data-id="element-1908">11:30</span>
                      </div>
                      <div className="flex items-center col-span-2" data-id="element-1909">
                        <span className="text-gray-700 font-medium" data-id="element-1910">With:</span>
                        <span className="ml-1" data-id="element-1911">Andrea M. (Host)</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-1912">
                    <button className="bg-black text-white text-xs px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors flex items-center" data-id="element-1913">
                      <Coffee className="h-3.5 w-3.5 mr-1" data-id="element-1914" />
                      Join
                    </button>
                    <button className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1915">
                      Reschedule
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow" data-id="element-1916">
              <div className="flex items-start gap-3" data-id="element-1917">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-1918">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gou2Ede34nLvCQizNZLz4q/irene-strong--FOUPtqP-mY-unsplash.jpg" alt="Giulia V." className="w-full h-full object-cover" data-id="element-1919" />
                </div>
                <div className="flex-1" data-id="element-1920">
                  <div className="mb-2" data-id="element-1921">
                    <h3 className="font-medium text-base mb-1" data-id="element-1922">
                      Coffee Break: Green Tech
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-1923">
                      Technologies for environmental sustainability
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mb-3" data-id="element-1924">
                    <span className="bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full" data-id="element-1925">
                      IT
                    </span>
                    <span className="bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full" data-id="element-1926">
                      Eco-sustainability
                    </span>
                  </div>
                  <div className="border-t border-gray-100 pt-2 mb-3" data-id="element-1927">
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600" data-id="element-1928">
                      <div className="flex items-center col-span-2" data-id="element-1929">
                        <Calendar className="h-3.5 w-3.5 mr-1 flex-shrink-0" data-id="element-1930" />
                        <span data-id="element-1931">Select date</span>
                      </div>
                      <div className="flex items-center col-span-2" data-id="element-1932">
                        <span className="text-gray-700 font-medium" data-id="element-1933">With:</span>
                        <span className="ml-1" data-id="element-1934">Giulia V. (Host)</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-1935">
                    <button className="bg-black text-white text-xs px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors flex items-center" data-id="element-1936">
                      <Coffee className="h-3.5 w-3.5 mr-1" data-id="element-1937" />
                      Join
                    </button>
                    <button className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-1938">
                      Reschedule
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 'lunch':
        return <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1939">
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow" data-id="element-1940">
              <div className="flex justify-between items-center border-b border-gray-100 px-4 py-3" data-id="element-1941">
                <h3 className="font-medium text-base" data-id="element-1942">
                  Innovation in Digital Marketing
                </h3>
                <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full whitespace-nowrap" data-id="element-1943">
                  Open
                </span>
              </div>
              <div className="p-4" data-id="element-1944">
                <div className="mb-4" data-id="element-1945">
                  <p className="text-sm text-gray-600 mb-3" data-id="element-1946">
                    Discussion on new trends
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-1947">
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-1948">
                      Marketing
                    </span>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-1949">
                      Digital
                    </span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-3 mb-4" data-id="element-1950">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-600" data-id="element-1951">
                    <div className="flex items-center" data-id="element-1952">
                      <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-500 flex-shrink-0" data-id="element-1953" />
                      <span data-id="element-1954">Thursday 28/10</span>
                    </div>
                    <div className="flex items-center" data-id="element-1955">
                      <Clock className="h-3.5 w-3.5 mr-1.5 text-gray-500 flex-shrink-0" data-id="element-1956" />
                      <span data-id="element-1957">13:00</span>
                    </div>
                    <div className="flex items-center" data-id="element-1958">
                      <MapPin className="h-3.5 w-3.5 mr-1.5 text-gray-500 flex-shrink-0" data-id="element-1959" />
                      <span data-id="element-1960">Lunch Room - Rome</span>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-3" data-id="element-1961">
                  <div className="flex items-center justify-between" data-id="element-1962">
                    <div className="flex items-center" data-id="element-1963">
                      <div className="flex -space-x-2 mr-2" data-id="element-1964">
                        <img className="w-7 h-7 rounded-full border-2 border-white" src="https://randomuser.me/api/portraits/men/32.jpg" alt="" data-id="element-1965" />
                        <img className="w-7 h-7 rounded-full border-2 border-white" src="https://randomuser.me/api/portraits/women/44.jpg" alt="" data-id="element-1966" />
                        <img className="w-7 h-7 rounded-full border-2 border-white" src="https://randomuser.me/api/portraits/men/85.jpg" alt="" data-id="element-1967" />
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-1968">
                        3 participants (max 8)
                      </span>
                    </div>
                    <button className="bg-black text-white text-xs px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-1969">
                      Join
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow" data-id="element-1970">
              <div className="flex justify-between items-center border-b border-gray-100 px-4 py-3" data-id="element-1971">
                <h3 className="font-medium text-base" data-id="element-1972">Blockchain and Web3</h3>
                <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full whitespace-nowrap" data-id="element-1973">
                  Confirmed
                </span>
              </div>
              <div className="p-4" data-id="element-1974">
                <div className="mb-4" data-id="element-1975">
                  <p className="text-sm text-gray-600 mb-3" data-id="element-1976">
                    Practical applications for business
                  </p>
                  <div className="flex flex-wrap gap-2" data-id="element-1977">
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-1978">
                      Blockchain
                    </span>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-1979">
                      Web3
                    </span>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-3 mb-4" data-id="element-1980">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-600" data-id="element-1981">
                    <div className="flex items-center" data-id="element-1982">
                      <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-500 flex-shrink-0" data-id="element-1983" />
                      <span data-id="element-1984">Tuesday 26/10</span>
                    </div>
                    <div className="flex items-center" data-id="element-1985">
                      <Clock className="h-3.5 w-3.5 mr-1.5 text-gray-500 flex-shrink-0" data-id="element-1986" />
                      <span data-id="element-1987">13:30</span>
                    </div>
                    <div className="flex items-center" data-id="element-1988">
                      <MapPin className="h-3.5 w-3.5 mr-1.5 text-gray-500 flex-shrink-0" data-id="element-1989" />
                      <span data-id="element-1990">Lunch Room - Milan</span>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-100 pt-3" data-id="element-1991">
                  <div className="flex items-center justify-between" data-id="element-1992">
                    <div className="flex items-center" data-id="element-1993">
                      <div className="flex -space-x-2 mr-2" data-id="element-1994">
                        <img className="w-7 h-7 rounded-full border-2 border-white" src="https://randomuser.me/api/portraits/men/32.jpg" alt="" data-id="element-1995" />
                        <img className="w-7 h-7 rounded-full border-2 border-white" src="https://uploadthingy.s3.us-west-1.amazonaws.com/rpDp7vdFb4PQ6LshFeTqC4/christian-buehner-zYD6ou1Qj10-unsplash.jpg" alt="" data-id="element-1996" />
                        <img className="w-7 h-7 rounded-full border-2 border-white" src="https://uploadthingy.s3.us-west-1.amazonaws.com/37RNAboQN8qk3BnTK1YzhT/irene-strong-TMt3JGoVlng-unsplash.jpg" alt="" data-id="element-1997" />
                        <img className="w-7 h-7 rounded-full border-2 border-white" src="https://uploadthingy.s3.us-west-1.amazonaws.com/gou2Ede34nLvCQizNZLz4q/irene-strong--FOUPtqP-mY-unsplash.jpg" alt="" data-id="element-1998" />
                        <div className="w-7 h-7 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs font-medium" data-id="element-1999">
                          +2
                        </div>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-2000">
                        6 participants (max 8)
                      </span>
                    </div>
                    <div className="flex items-center" data-id="element-2001">
                      <span className="text-xs text-gray-700 flex items-center" data-id="element-2002">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" data-id="element-2003" />
                        Participant
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 'call':
        return <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2004">
            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow" data-id="element-2005">
              <div className="flex items-start gap-3" data-id="element-2006">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-2007">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/iiGCarC4BpomqUoZERcuSH/jurica-koletic-7YVZYZeITc8-unsplash.jpg" alt="Laura R." className="w-full h-full object-cover" data-id="element-2008" />
                </div>
                <div className="flex-1" data-id="element-2009">
                  <div className="flex justify-between items-start" data-id="element-2010">
                    <div data-id="element-2011">
                      <h3 className="font-medium text-base mb-1" data-id="element-2012">
                        Call with Laura R.
                      </h3>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-2013">
                        Product strategy for Q4
                      </p>
                    </div>
                    <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full" data-id="element-2014">
                      Scheduled
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-3" data-id="element-2015">
                    <div className="flex items-center" data-id="element-2016">
                      <Calendar className="h-3.5 w-3.5 mr-1" data-id="element-2017" />
                      <span data-id="element-2018">Friday 29/10</span>
                    </div>
                    <div className="flex items-center" data-id="element-2019">
                      <Clock className="h-3.5 w-3.5 mr-1" data-id="element-2020" />
                      <span data-id="element-2021">15:00</span>
                    </div>
                    <div className="flex items-center" data-id="element-2022">
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Q7pENZPXA4vLneeS5ebyU/images_%281%29.png" alt="Google Meet" className="h-[67px] w-[84px] object-contain" data-id="element-2023" />
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-2024">
                    <button className="bg-black text-white text-xs px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors flex items-center" data-id="element-2025">
                      <Video className="h-3.5 w-3.5 mr-1" data-id="element-2026" />
                      Join
                    </button>
                    <button className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2027">
                      Reschedule
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow" data-id="element-2028">
              <div className="flex items-start gap-3" data-id="element-2029">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-2030">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bc8MUYcKAT6QrAiyMeUwXx/christian-buehner-DItYlc26zVI-unsplash.jpg" alt="Andrea M." className="w-full h-full object-cover" data-id="element-2031" />
                </div>
                <div className="flex-1" data-id="element-2032">
                  <div className="flex justify-between items-start" data-id="element-2033">
                    <div data-id="element-2034">
                      <h3 className="font-medium text-base mb-1" data-id="element-2035">
                        Call with Andrea M.
                      </h3>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-2036">
                        Data analysis for project XYZ
                      </p>
                    </div>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-2037">
                      Completed
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-3" data-id="element-2038">
                    <div className="flex items-center" data-id="element-2039">
                      <Calendar className="h-3.5 w-3.5 mr-1" data-id="element-2040" />
                      <span data-id="element-2041">Monday 18/10</span>
                    </div>
                    <div className="flex items-center" data-id="element-2042">
                      <Clock className="h-3.5 w-3.5 mr-1" data-id="element-2043" />
                      <span data-id="element-2044">11:30</span>
                    </div>
                    <div className="flex items-center" data-id="element-2045">
                      <img src="https://upload.wikimedia.org/wikipedia/commons/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg" alt="Microsoft Teams" className="h-3.5 w-3.5" data-id="element-2046" />
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-2047">
                    <button className="bg-black text-white text-xs px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors flex items-center" data-id="element-2048">
                      <MessageSquare className="h-3.5 w-3.5 mr-1" data-id="element-2049" />
                      AI Summary
                    </button>
                    <button className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2050">
                      Follow-up
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow" data-id="element-2051">
              <div className="flex items-start gap-3" data-id="element-2052">
                <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-2053">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gou2Ede34nLvCQizNZLz4q/irene-strong--FOUPtqP-mY-unsplash.jpg" alt="Giulia V." className="w-full h-full object-cover" data-id="element-2054" />
                </div>
                <div className="flex-1" data-id="element-2055">
                  <div className="flex justify-between items-start" data-id="element-2056">
                    <div data-id="element-2057">
                      <h3 className="font-medium text-base mb-1" data-id="element-2058">
                        Call with Giulia V.
                      </h3>
                      <p className="text-sm text-gray-600 mb-2" data-id="element-2059">
                        UX review of client project
                      </p>
                    </div>
                    <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full" data-id="element-2060">
                      Proposed
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-3" data-id="element-2061">
                    <div className="flex items-center" data-id="element-2062">
                      <Calendar className="h-3.5 w-3.5 mr-1" data-id="element-2063" />
                      <span data-id="element-2064">Select date</span>
                    </div>
                    <div className="flex items-center" data-id="element-2065">
                      <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3Q7pENZPXA4vLneeS5ebyU/images_%281%29.png" alt="Google Meet" className="h-[67px] w-[84px] object-contain" data-id="element-2066" />
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-2067">
                    <button className="bg-black text-white text-xs px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors flex items-center" data-id="element-2068">
                      <Calendar className="h-3.5 w-3.5 mr-1" data-id="element-2069" />
                      Schedule
                    </button>
                    <button className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-2070">
                      Decline
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 'workshop':
        return <div className="grid grid-cols-1 gap-4" data-id="element-2071">
            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow" data-id="element-2072">
              <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-2073">
                <div className="w-full md:w-48 h-32 md:h-24 rounded-lg overflow-hidden flex-shrink-0" data-id="element-2074">
                  <img src="https://images.unsplash.com/photo-1558403194-611308249627?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80" alt="Workshop UX/UI" className="w-full h-full object-cover" data-id="element-2075" />
                </div>
                <div className="flex-1" data-id="element-2076">
                  <div className="flex justify-between items-start mb-2" data-id="element-2077">
                    <div data-id="element-2078">
                      <h3 className="font-medium text-lg mb-1" data-id="element-2079">
                        Design System Workshop
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-2080">
                        Creating a scalable design system for digital products
                      </p>
                    </div>
                    <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full" data-id="element-2081">
                      Open
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-3" data-id="element-2082">
                    <div className="flex items-center" data-id="element-2083">
                      <Calendar className="h-3.5 w-3.5 mr-1" data-id="element-2084" />
                      <span data-id="element-2085">November 5</span>
                    </div>
                    <div className="flex items-center" data-id="element-2086">
                      <Clock className="h-3.5 w-3.5 mr-1" data-id="element-2087" />
                      <span data-id="element-2088">10:00 - 16:00</span>
                    </div>
                    <div className="flex items-center" data-id="element-2089">
                      <MapPin className="h-3.5 w-3.5 mr-1" data-id="element-2090" />
                      <span data-id="element-2091">Milan + Rome (online)</span>
                    </div>
                    <div className="flex items-center" data-id="element-2092">
                      <User className="h-3.5 w-3.5 mr-1" data-id="element-2093" />
                      <span data-id="element-2094">Marco B. (facilitator)</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-2095">
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-2096">
                      Design
                    </span>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-2097">
                      UX/UI
                    </span>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-2098">
                      Product
                    </span>
                  </div>
                  <div className="flex items-center" data-id="element-2099">
                    <div className="flex -space-x-2" data-id="element-2100">
                      <img className="w-7 h-7 rounded-full border-2 border-white" src="https://uploadthingy.s3.us-west-1.amazonaws.com/rpDp7vdFb4PQ6LshFeTqC4/christian-buehner-zYD6ou1Qj10-unsplash.jpg" alt="" data-id="element-2101" />
                      <img className="w-7 h-7 rounded-full border-2 border-white" src="https://uploadthingy.s3.us-west-1.amazonaws.com/gou2Ede34nLvCQizNZLz4q/irene-strong--FOUPtqP-mY-unsplash.jpg" alt="" data-id="element-2102" />
                      <img className="w-7 h-7 rounded-full border-2 border-white" src="https://uploadthingy.s3.us-west-1.amazonaws.com/37RNAboQN8qk3BnTK1YzhT/irene-strong-TMt3JGoVlng-unsplash.jpg" alt="" data-id="element-2103" />
                      <div className="w-7 h-7 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs font-medium" data-id="element-2104">
                        +8
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 ml-2" data-id="element-2105">
                      11 participants (max 20)
                    </span>
                    <button className="ml-auto bg-black text-white text-xs px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-2106">
                      Join
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow" data-id="element-2107">
              <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-2108">
                <div className="w-full md:w-48 h-32 md:h-24 rounded-lg overflow-hidden flex-shrink-0" data-id="element-2109">
                  <img src="https://images.unsplash.com/photo-1591522810850-58128c5fb089?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80" alt="Workshop AI" className="w-full h-full object-cover" data-id="element-2110" />
                </div>
                <div className="flex-1" data-id="element-2111">
                  <div className="flex justify-between items-start mb-2" data-id="element-2112">
                    <div data-id="element-2113">
                      <h3 className="font-medium text-lg mb-1" data-id="element-2114">
                        AI for Business Leaders
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-2115">
                        How to integrate AI into business processes
                      </p>
                    </div>
                    <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full" data-id="element-2116">
                      Coming soon
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-3" data-id="element-2117">
                    <div className="flex items-center" data-id="element-2118">
                      <Calendar className="h-3.5 w-3.5 mr-1" data-id="element-2119" />
                      <span data-id="element-2120">November 12</span>
                    </div>
                    <div className="flex items-center" data-id="element-2121">
                      <Clock className="h-3.5 w-3.5 mr-1" data-id="element-2122" />
                      <span data-id="element-2123">14:00 - 17:00</span>
                    </div>
                    <div className="flex items-center" data-id="element-2124">
                      <MapPin className="h-3.5 w-3.5 mr-1" data-id="element-2125" />
                      <span data-id="element-2126">All locations (online)</span>
                    </div>
                    <div className="flex items-center" data-id="element-2127">
                      <User className="h-3.5 w-3.5 mr-1" data-id="element-2128" />
                      <span data-id="element-2129">Andrea M. (facilitator)</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-2130">
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-2131">
                      AI
                    </span>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-2132">
                      Business
                    </span>
                    <span className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-full" data-id="element-2133">
                      Innovation
                    </span>
                  </div>
                  <div className="flex items-center" data-id="element-2134">
                    <div className="flex -space-x-2" data-id="element-2135">
                      <img className="w-7 h-7 rounded-full border-2 border-white" src="https://uploadthingy.s3.us-west-1.amazonaws.com/bc8MUYcKAT6QrAiyMeUwXx/christian-buehner-DItYlc26zVI-unsplash.jpg" alt="" data-id="element-2136" />
                      <img className="w-7 h-7 rounded-full border-2 border-white" src="https://uploadthingy.s3.us-west-1.amazonaws.com/iiGCarC4BpomqUoZERcuSH/jurica-koletic-7YVZYZeITc8-unsplash.jpg" alt="" data-id="element-2137" />
                      <div className="w-7 h-7 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-xs font-medium" data-id="element-2138">
                        +3
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 ml-2" data-id="element-2139">
                      5 participants (max 30)
                    </span>
                    <button className="ml-auto bg-black text-white text-xs px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-2140">
                      Join
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      default:
        return <div data-id="element-2141">Mode not found</div>;
    }
  };
  return <div className="flex flex-col min-h-screen w-full bg-white text-black font-sans" data-id="element-2142">
      {/* Main content */}
      <div className="flex-grow bg-white pt-6 pb-20 md:pb-10" data-id="element-2143">
        <div className="max-w-6xl mx-auto px-0" data-id="element-2144">
          {/* Breadcrumbs navigation */}
          <nav className="flex items-center text-sm mb-6" data-id="element-2145">
            <button onClick={() => navigate('/networking')} className="flex items-center text-gray-600 hover:text-black" data-id="element-2146">
              <ArrowLeft className="h-4 w-4 mr-1" data-id="element-2147" />
              <span data-id="element-2148">Networking</span>
            </button>
            <ChevronRight className="h-4 w-4 mx-2 text-gray-400" data-id="element-2149" />
            <span className="text-black font-medium" data-id="element-2150">{currentMode.title}</span>
          </nav>
          {/* Action buttons row */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4" data-id="element-2151">
            <div className="flex items-center space-x-2" data-id="element-2152">
              <button className={`px-4 py-2 text-sm font-medium rounded-full ${activeTab === 'all' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setActiveTab('all')} data-id="element-2153">
                All opportunities
              </button>
              <button className={`px-4 py-2 text-sm font-medium rounded-full ${activeTab === 'my' ? 'bg-black text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} onClick={() => setActiveTab('my')} data-id="element-2154">
                Requests
              </button>
            </div>
            <div className="flex items-center gap-3 w-full md:w-auto" data-id="element-2155">
              <div className="relative flex-grow md:flex-grow-0 md:w-64" data-id="element-2156">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2157">
                  <Search className="h-4 w-4 text-gray-400" data-id="element-2158" />
                </div>
                <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2159" />
              </div>
              <button className="flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" onClick={() => setShowFilters(true)} data-id="element-2160">
                <Filter className="h-4 w-4 md:mr-2" data-id="element-2161" />
                <span className="hidden md:inline" data-id="element-2162">Filters</span>
              </button>
              <button onClick={openCreateModal} className="flex items-center justify-center px-3 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors" data-id="element-2163">
                <Plus className="h-4 w-4 md:mr-2" data-id="element-2164" />
                <span className="hidden md:inline" data-id="element-2165">New</span>
              </button>
            </div>
          </div>
          {/* Content based on mode */}
          {renderContent()}
        </div>
      </div>
      {/* Mobile bottom navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around py-3 z-50" data-id="element-2166">
        <button onClick={() => navigate('/')} className="flex flex-col items-center" data-id="element-2167">
          <Home className="w-5 h-5 text-gray-400" data-id="element-2168" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-2169">Home</span>
        </button>
        <button onClick={() => navigate('/connections')} className="flex flex-col items-center" data-id="element-2170">
          <Users className="w-5 h-5 text-gray-400" data-id="element-2171" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-2172">Connections</span>
        </button>
        <button className="flex flex-col items-center" data-id="element-2173">
          <Globe className="w-5 h-5 text-black" data-id="element-2174" />
          <span className="text-xs mt-1 text-black" data-id="element-2175">Networking</span>
        </button>
        <button onClick={() => navigate('/collaboration')} className="flex flex-col items-center" data-id="element-2176">
          <Briefcase className="w-5 h-5 text-gray-400" data-id="element-2177" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-2178">Collaboration</span>
        </button>
        <button onClick={() => navigate('/impact')} className="flex flex-col items-center" data-id="element-2179">
          <Leaf className="w-5 h-5 text-gray-400" data-id="element-2180" />
          <span className="text-xs mt-1 text-gray-400" data-id="element-2181">Impact</span>
        </button>
      </div>
      {/* Filters Modal */}
      {showFilters && <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-2182">
          <div className="fixed inset-0 bg-black/30 transition-opacity" onClick={() => setShowFilters(false)} data-id="element-2183"></div>
          <div className="bg-white w-full max-w-md rounded-xl overflow-hidden transform transition-all duration-300 ease-out animate-slide-up mx-4" data-id="element-2184">
            <div className="p-6" data-id="element-2185">
              <div className="flex justify-between items-center mb-4" data-id="element-2186">
                <h3 className="font-medium text-lg" data-id="element-2187">Filter opportunities</h3>
                <div className="flex items-center gap-3" data-id="element-2188">
                  <button className="text-sm text-gray-500 hover:text-gray-700" onClick={resetFilters} data-id="element-2189">
                    Reset filters
                  </button>
                  <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowFilters(false)} data-id="element-2190">
                    <X className="h-5 w-5" data-id="element-2191" />
                  </button>
                </div>
              </div>
              <div className="space-y-4" data-id="element-2192">
                {/* Status filter */}
                <div data-id="element-2193">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2194">
                    Status
                  </label>
                  <div className="relative" data-id="element-2195">
                    <select className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} data-id="element-2196">
                      <option value="" data-id="element-2197">All statuses</option>
                      <option value="open" data-id="element-2198">Open</option>
                      <option value="confirmed" data-id="element-2199">Confirmed</option>
                      <option value="suggested" data-id="element-2200">Suggested</option>
                      <option value="completed" data-id="element-2201">Completed</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-2202">
                      <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-2203" />
                    </div>
                  </div>
                </div>
                {/* Location filter */}
                <div data-id="element-2204">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2205">
                    Location
                  </label>
                  <div className="relative" data-id="element-2206">
                    <select className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" value={filters.location} onChange={e => handleFilterChange('location', e.target.value)} data-id="element-2207">
                      <option value="" data-id="element-2208">All locations</option>
                      {locations.map(location => <option key={location} value={location} data-id="element-2209">
                          {location}
                        </option>)}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-2210">
                      <ChevronDown className="h-4 w-4 text-gray-400" data-id="element-2211" />
                    </div>
                  </div>
                </div>
                <button className="w-full mt-4 bg-black text-white py-2.5 rounded-lg hover:bg-gray-800 transition-colors" onClick={() => setShowFilters(false)} data-id="element-2212">
                  Apply filters
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Create Modal */}
      {showCreateModal && <div className="fixed inset-0 z-50 flex items-center justify-center" data-id="element-2213">
          <div className="fixed inset-0 bg-black/30 transition-opacity" onClick={closeModal} data-id="element-2214"></div>
          <div className="bg-white w-full max-w-md rounded-xl overflow-hidden transform transition-all duration-300 ease-out animate-slide-up mx-4" data-id="element-2215">
            <div className="p-6" data-id="element-2216">
              <div className="flex justify-between items-center mb-6" data-id="element-2217">
                <h3 className="font-medium text-xl font-sharp-grotesk" data-id="element-2218">
                  {currentMode.modalTitle}
                </h3>
                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700" data-id="element-2219">
                  <X className="h-5 w-5" data-id="element-2220" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-2221">
                {/* Title */}
                <div data-id="element-2222">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2223">
                    Title
                  </label>
                  <input type="text" id="title" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" placeholder={mode === 'coffee' ? 'Coffee with...' : mode === 'lunch' ? 'Topic Lunch on...' : mode === 'call' ? 'Call with...' : 'Workshop on...'} data-id="element-2224" />
                </div>
                {/* Description */}
                <div data-id="element-2225">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2226">
                    Description
                  </label>
                  <textarea id="description" rows={3} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" placeholder="Briefly describe the activity..." data-id="element-2227"></textarea>
                </div>
                {/* Date and Time */}
                <div className="grid grid-cols-2 gap-3" data-id="element-2228">
                  <div data-id="element-2229">
                    <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2230">
                      Date
                    </label>
                    <input type="date" id="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" data-id="element-2231" />
                  </div>
                  <div data-id="element-2232">
                    <label htmlFor="time" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2233">
                      Time
                    </label>
                    <input type="time" id="time" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" data-id="element-2234" />
                  </div>
                </div>
                {/* Location */}
                <div data-id="element-2235">
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2236">
                    Location
                  </label>
                  <select id="location" className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" data-id="element-2237">
                    <option value="" data-id="element-2238">Select a location</option>
                    {locations.map(location => <option key={location} value={location} data-id="element-2239">
                        {location}
                      </option>)}
                    {mode === 'call' && <option value="online" data-id="element-2240">Online</option>}
                    {mode === 'workshop' && <option value="all" data-id="element-2241">All locations (online)</option>}
                  </select>
                </div>
                {/* Topics/Tags */}
                {(mode === 'lunch' || mode === 'workshop') && <div data-id="element-2242">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2243">
                      Topics
                    </label>
                    <div className="flex flex-wrap gap-2 border border-gray-300 rounded-md p-2" data-id="element-2244">
                      {topics.slice(0, 6).map(topic => <button key={topic} className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200" data-id="element-2245">
                          {topic}
                        </button>)}
                      <button className="px-2 py-1 text-xs rounded-full bg-black text-white hover:bg-gray-800" data-id="element-2246">
                        + Add
                      </button>
                    </div>
                  </div>}
                {/* Participants (for Topic Lunch and Workshop) */}
                {(mode === 'lunch' || mode === 'workshop') && <div data-id="element-2247">
                    <label htmlFor="max-participants" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2248">
                      Maximum number of participants
                    </label>
                    <input type="number" id="max-participants" min="2" max="30" defaultValue={mode === 'lunch' ? '8' : '20'} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" data-id="element-2249" />
                  </div>}
                {/* Person selection (for Coffee and Call) */}
                {(mode === 'coffee' || mode === 'call') && <div data-id="element-2250">
                    <label htmlFor="person" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2251">
                      Person
                    </label>
                    <div className="relative" data-id="element-2252">
                      <input type="text" id="person" className="block w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-black focus:border-black" placeholder="Search for a person..." data-id="element-2253" />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2254">
                        <Search className="h-4 w-4 text-gray-400" data-id="element-2255" />
                      </div>
                    </div>
                    <div className="mt-2 bg-gray-50 rounded-md p-2 max-h-32 overflow-y-auto" data-id="element-2256">
                      <div className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-md cursor-pointer" data-id="element-2257">
                        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/gou2Ede34nLvCQizNZLz4q/irene-strong--FOUPtqP-mY-unsplash.jpg" alt="Giulia V." className="w-8 h-8 rounded-full object-cover" data-id="element-2258" />
                        <div data-id="element-2259">
                          <p className="text-sm font-medium" data-id="element-2260">Giulia Verdi</p>
                          <p className="text-xs text-gray-500" data-id="element-2261">Web Developer</p>
                        </div>
                        <span className="ml-auto text-xs bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-2262">
                          92% match
                        </span>
                      </div>
                      <div className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-md cursor-pointer" data-id="element-2263">
                        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/rpDp7vdFb4PQ6LshFeTqC4/christian-buehner-zYD6ou1Qj10-unsplash.jpg" alt="Marco B." className="w-8 h-8 rounded-full object-cover" data-id="element-2264" />
                        <div data-id="element-2265">
                          <p className="text-sm font-medium" data-id="element-2266">Marco Bianchi</p>
                          <p className="text-xs text-gray-500" data-id="element-2267">UX Designer</p>
                        </div>
                        <span className="ml-auto text-xs bg-gray-100 px-2 py-0.5 rounded-full" data-id="element-2268">
                          85% match
                        </span>
                      </div>
                    </div>
                  </div>}
                {/* AI Assistant option for Call */}
                {mode === 'call' && <div className="flex items-center" data-id="element-2269">
                    <input type="checkbox" id="ai-assistant" className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded" defaultChecked data-id="element-2270" />
                    <label htmlFor="ai-assistant" className="ml-2 block text-sm text-gray-700" data-id="element-2271">
                      Enable AI Assistant for summaries and suggestions
                    </label>
                    <HelpCircle className="h-4 w-4 text-gray-400 ml-1 cursor-help" data-id="element-2272" />
                  </div>}
                <button className="w-full mt-4 bg-black text-white py-2.5 rounded-lg hover:bg-gray-800 transition-colors" data-id="element-2273">
                  {mode === 'coffee' && 'Create Coffee Break'}
                  {mode === 'lunch' && 'Create Topic Lunch'}
                  {mode === 'call' && 'Create Call 1-to-1'}
                  {mode === 'workshop' && 'Create Workshop'}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
export default {
  NetworkingDetailPage
};